import { useMemo, useState } from 'react';

import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';

interface UseDatePickerTriggerOptions {
  limitHeightToViewport?: boolean;
}

export const useDatePickerTrigger = ({ limitHeightToViewport }: UseDatePickerTriggerOptions) => {
  const [isOpen, setIsOpen] = useState(false);

  const floatingMiddleware = useMemo(() => {
    const middleware = [
      offset(8),
      flip({
        fallbackPlacements: ['bottom-end', 'top-start', 'top-end'],
      }),
      shift({
        padding: 8,
      }),
    ];

    if (limitHeightToViewport) {
      middleware.push(
        size({
          padding: 16,
          apply({ availableHeight, elements }) {
            const currentHeight = elements.floating.getBoundingClientRect().height;
            // 현재 높이가 가용 높이보다 작거나 같으면 리턴
            if (currentHeight <= availableHeight) {
              return;
            }

            Object.assign(elements.floating.style, {
              height: `${availableHeight}px`,
            });

            if (elements.floating.children[0] instanceof HTMLElement) {
              elements.floating.children[0].style.height = '100%';
            }
          },
        }),
      );
    }

    return middleware;
  }, [limitHeightToViewport]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: floatingMiddleware,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  const handleClose = () => {
    setIsOpen(false);
  };

  return {
    isOpen,
    setIsOpen,
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    handleClose,
  };
};
